// src/pages/AccountTenantRolesPage.js

import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Collapse,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

const AccountTenantRolesPage = () => {
  // Inyectamos el header tenantid en cada petición
  const tenantid = localStorage.getItem('tenantid');
  useEffect(() => {
    api.defaults.headers.common['tenantid'] = tenantid;
  }, [tenantid]);

  // Estado principal
  const [accountsData, setAccountsData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);

  // Filtros
  const [filterAccountName, setFilterAccountName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterTenantName, setFilterTenantName] = useState('');
  const [filterRoleName, setFilterRoleName] = useState('');

  // Paginación
  const [page, setPage] = useState(1);
  const limit = 50;

  // Expand collapse
  const [expanded, setExpanded] = useState({});

  // Modales y formularios
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [formAccount, setFormAccount] = useState('');
  const [formTenant, setFormTenant] = useState('');
  const [formRole, setFormRole] = useState('');
  const [editContext, setEditContext] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Carga selects (accounts, tenants, roles)
  useEffect(() => {
    api.get('/accounts')
      .then(res => setAccounts(res.data || []))
      .catch(() => setAccounts([]));

    api.get('/tenants')
      .then(res => setTenants(res.data.tenants || []))
      .catch(() => setTenants([]));

    api.get('/roles')
      .then(res => setRoles(res.data.data || []))
      .catch(() => setRoles([]));
  }, []);

  // Carga agrupada
  useEffect(() => {
    api.get('/account_tenant_roles', {
      params: {
        account_name: filterAccountName || undefined,
        email:        filterEmail       || undefined,
        tenant_name:  filterTenantName  || undefined,
        role_name:    filterRoleName    || undefined,
        page,
        limit,
        sort: 'account_name:asc'
      }
    })
    .then(res => setAccountsData(res.data.data || []))
    .catch(() => setAccountsData([]));
  }, [filterAccountName, filterEmail, filterTenantName, filterRoleName, page]);

  const toggleExpand = id =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const resetForm = () => {
    setFormAccount('');
    setFormTenant('');
    setFormRole('');
    setEditContext(null);
  };

  const handleAdd = () => {
    api.post('/account_tenant_roles',
      { account_id: formAccount, tenant_id: formTenant, role_id: formRole }
    )
    .then(() => {
      setSnackbar({ open: true, message: 'Asignación creada', severity: 'success' });
      setOpenAdd(false);
      resetForm();
      setPage(1);
    })
    .catch(err => {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    });
  };

  const openEditDialog = (accountId, assignment) => {
    setEditContext({ accountId, ...assignment });
    setFormRole(assignment.role_id);
    setOpenEdit(true);
  };

  const handleEdit = () => {
    const { accountId, tenant_id, role_id } = editContext;
    api.put(
      `/account_tenant_roles/${accountId}/${tenant_id}/${role_id}`,
      { new_role_id: formRole }
    )
    .then(() => {
      setSnackbar({ open: true, message: 'Asignación actualizada', severity: 'success' });
      setOpenEdit(false);
      resetForm();
    })
    .catch(err => {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    });
  };

  const handleDelete = (accountId, assignment) => {
    if (!window.confirm('¿Eliminar esta asignación?')) return;
    api.delete(`/account_tenant_roles/${accountId}/${assignment.tenant_id}/${assignment.role_id}`)
      .then(() => {
        setSnackbar({ open: true, message: 'Asignación eliminada', severity: 'success' });
      })
      .catch(err => {
        setSnackbar({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
      });
  };

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Cuentas y Asignaciones</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)}>
          Nueva Asignación
        </Button>
      </Box>

      {/* Filtros */}
      <Box mb={2} display="flex" gap={2} flexWrap="wrap">
        <TextField label="Cuenta" size="small"
          value={filterAccountName}
          onChange={e => setFilterAccountName(e.target.value)}
        />
        <TextField label="Email" size="small"
          value={filterEmail}
          onChange={e => setFilterEmail(e.target.value)}
        />
        <TextField label="Tenant" size="small"
          value={filterTenantName}
          onChange={e => setFilterTenantName(e.target.value)}
        />
        <TextField label="Rol" size="small"
          value={filterRoleName}
          onChange={e => setFilterRoleName(e.target.value)}
        />
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Cuenta</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right"># Asignaciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountsData.map(row => (
                <React.Fragment key={row.account_id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleExpand(row.account_id)}>
                        {expanded[row.account_id]
                          ? <ExpandLessIcon />
                          : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.account_name}</TableCell>
                    <TableCell>{row.account_email}</TableCell>
                    <TableCell align="right">{row.assignments.length}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={4}>
                      <Collapse in={expanded[row.account_id]} timeout="auto" unmountOnExit>
                        <Box margin={1}>
                          <Typography variant="subtitle2" gutterBottom>
                            Asignaciones
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Tenant</TableCell>
                                <TableCell>Rol</TableCell>
                                <TableCell>Asignada en</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.assignments.map(a => (
                                <TableRow key={`${a.tenant_id}-${a.role_id}`}>
                                  <TableCell>{a.tenant_name}</TableCell>
                                  <TableCell>{a.role_name}</TableCell>
                                  <TableCell>{new Date(a.assigned_at).toLocaleString()}</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => openEditDialog(row.account_id, a)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(row.account_id, a)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {row.assignments.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    Sin asignaciones
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {accountsData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No hay cuentas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Paginación */}
      <Box display="flex" justifyContent="space-between" mt={2}>
        <Button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
          Anterior
        </Button>
        <Button
          disabled={accountsData.length < limit}
          onClick={() => setPage(p => p + 1)}
        >
          Siguiente
        </Button>
      </Box>

      {/* Modal Crear */}
      <Dialog open={openAdd} onClose={() => { setOpenAdd(false); resetForm(); }}>
        <DialogTitle>Nueva Asignación</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Cuenta</InputLabel>
            <Select
              value={formAccount}
              onChange={e => setFormAccount(e.target.value)}
              label="Cuenta"
            >
              {accounts.map(acc => (
                <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Tenant</InputLabel>
            <Select
              value={formTenant}
              onChange={e => setFormTenant(e.target.value)}
              label="Tenant"
            >
              {tenants.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Rol</InputLabel>
            <Select
              value={formRole}
              onChange={e => setFormRole(e.target.value)}
              label="Rol"
            >
              {roles.map(r => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAdd}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar */}
      <Dialog open={openEdit} onClose={() => { setOpenEdit(false); resetForm(); }}>
        <DialogTitle>Editar Rol</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Nuevo Rol</InputLabel>
            <Select
              value={formRole}
              onChange={e => setFormRole(e.target.value)}
              label="Nuevo Rol"
            >
              {roles.map(r => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEdit}>Actualizar</Button>
        </DialogActions>
      </Dialog>

      {/* Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default AccountTenantRolesPage;
